import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { IoMdAdd } from "react-icons/io";
import { FaEdit, FaClone, FaChevronDown } from "react-icons/fa";
import './agencyList.css';
import AgenciesDS from "../../DataServices/AgenciesDs";
import { useCustomContext } from "../CustomComponents/CustomComponents";
import { GoProjectSymlink } from "react-icons/go";
import { CiSearch } from "react-icons/ci";
import { MdCancel } from "react-icons/md";
import { insertLog } from "../Logs/InsertLog";
import { LuTriangle } from "react-icons/lu";
import { TbTriangleFilled, TbTriangleInvertedFilled } from "react-icons/tb";

const Agencies = () => {
  const navigate = useNavigate();
  const agenciesDS = new AgenciesDS();
  const { stopHudRotation, hud, showToast, showAlert } = useCustomContext();

  const [agencyByPage, setAgencyByPage] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [loadedPages, setLoadedPages] = useState([]);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [pageSize, setPageSize] = useState(25);
  const [isPageSizeEnabled, setIsPageSizeEnabled] = useState(false);

  const [datasetList, setDatasetList] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState(localStorage.getItem("selectedDataset") || ""
  );
  const [datasetSearch, setDatasetSearch] = useState("");
  const [isDatasetDropdownOpen, setIsDatasetDropdownOpen] = useState(false);
  const [showAllDatasetOptions, setShowAllDatasetOptions] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');

  const [sortOrder, setSortOrder] = useState("asc");

  const [sortConfig, setSortConfig] = useState(null);

  const [requestBasedDatasets, setRequestBasedDatasets] = useState([]);

  // Navigate to add-agency page
  const addAgencies = () => {
    localStorage.removeItem("id");
    localStorage.removeItem("cloneId");
    localStorage.removeItem("isClone");
    navigate("/add-agencyDetails");
  };

  useEffect(() => {
    localStorage.removeItem("agencyDetailsPayload");
    fetchAllDatasets();
  }, []);

  useEffect(() => {
    if (!selectedDataset) return;

    const shouldRestore =
      localStorage.getItem("restoreAgencyPage") === "true";

    const pageToLoad = shouldRestore
      ? Number(localStorage.getItem("agencyPage")) || 1
      : 1;

    setCurrentPage(pageToLoad);
    if (!shouldRestore) {
      setAgencyByPage({});
      setLoadedPages([]);
      setHasMorePages(true);
    }

    fetchAllAgencies(
      pageToLoad,
      pageSize,
      true,
      selectedDataset,
      ""
    );

    if (shouldRestore) {
      localStorage.removeItem("restoreAgencyPage");
      localStorage.removeItem("agencyPage");
    }
  }, [selectedDataset]);

  useEffect(() => {
    if (!selectedDataset) return;

    const dataset = datasetList.find((ds) => ds.datasetID === selectedDataset);
    if (dataset) {
      setDatasetSearch(getDatasetLabel(dataset));
    }
  }, [selectedDataset, datasetList]);

  const getDatasetLabel = (dataset) =>
    dataset?.datasetName || dataset?.datasetID || "";

  const selectedDatasetRecord = datasetList.find((dataset) => dataset.datasetID === selectedDataset);
  const selectedDatasetLabel = selectedDatasetRecord ? getDatasetLabel(selectedDatasetRecord) : "";
  const datasetSearchText = selectedDataset && datasetSearch === selectedDatasetLabel
    ? ""
    : datasetSearch.trim().toLowerCase();

  const filteredDatasetOptions = showAllDatasetOptions
    ? datasetList
    : datasetSearchText
      ? datasetList.filter((dataset) =>
        getDatasetLabel(dataset).toLowerCase().includes(datasetSearchText)
      )
      : datasetList;

  const resetDatasetSelection = () => {
    setSelectedDataset("");
    localStorage.removeItem("selectedDataset");
    setCurrentPage(1);
    setAgencyByPage({});
    setLoadedPages([]);
    setHasMorePages(true);
    setIsPageSizeEnabled(false);
    setTotalRecords(0);
    setSearch("");
    setActiveSearch("");
  };

  const handleDatasetSearchChange = (e) => {
    const value = e.target.value;
    setDatasetSearch(value);
    setIsDatasetDropdownOpen(true);
    setShowAllDatasetOptions(false);

    if (selectedDataset || !value) {
      resetDatasetSelection();
    }
  };

  const selectDatasetOption = (dataset) => {
    const datasetID = dataset.datasetID;
    setSelectedDataset(datasetID);
    setDatasetSearch(getDatasetLabel(dataset));
    setSearch("");
    setActiveSearch("");
    setIsDatasetDropdownOpen(false);
    setShowAllDatasetOptions(false);
    localStorage.setItem("selectedDataset", datasetID);
    setTotalRecords(0);
  };

  const handleDatasetKeyDown = (e) => {
    if (e.key === "Enter" && filteredDatasetOptions.length > 0) {
      e.preventDefault();
      selectDatasetOption(filteredDatasetOptions[0]);
    }

    if (e.key === "Escape") {
      setIsDatasetDropdownOpen(false);
    }
  };


  const fetchAllAgencies = async (page, limit, reset = false, datasetID, searchText = "") => {
    if (!reset && loadedPages.includes(page)) return;

    const offset = (page - 1) * limit;
    const trimmedSearchText = String(searchText || "").trim();
    const request = { limit, offset, datasetID };

    if (trimmedSearchText) {
      request.searchText = trimmedSearchText;
    }

    hud("Please wait...");

    try {
      const response = await agenciesDS.AP_Agency_List_GET(request);
      const data = response.data;
      const body = typeof data.body === "string" ? JSON.parse(data.body) : data.body;

      if (data.statusCode === 200 && Array.isArray(body.records)) {

        setAgencyByPage(prev =>
          reset
            ? { [page]: body.records }
            : { ...prev, [page]: body.records }
        );

        setLoadedPages(prev =>
          reset ? [page] : prev.includes(page) ? prev : [...prev, page]
        );

        setHasMorePages(body.isMoreRecordsAvailable);
        setIsPageSizeEnabled(true);
        setTotalRecords(body.totalRecords);

      } else {
        showAlert(body?.message || "No agencies found", [
          { label: "OK", onClick: () => { }, color: 'var(--buttonColor)' }
        ]);
        insertLog(
          `${userName} failed to fetch agencies for dataset ${datasetID}`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          '',
          'GET',
          'AP_Agency_List_GET',
          'FAILED',
          'No agencies found'
        );
      }
    } catch (err) {
      insertLog(
        `${userName} failed to fetch agencies for dataset ${datasetID}`,
        0,
        useremail,
        hud,
        stopHudRotation,
        showAlert,
        '',
        'GET',
        'AP_Agency_List_GET',
        'FAILED',
        err.message
      );
      showAlert(err.message || "Something went wrong while fetching Agencies.", [{ label: "OK", onClick: () => { }, color: 'var(--buttonColor)' }]);
    } finally {
      stopHudRotation();
    }
  };



  const fetchAllDatasets = async () => {
    try {
      const req = {};
      const response = await agenciesDS.AP_Dataset_List_GET(req);

      const data = response.data;
      const body = typeof data.body === "string" ? JSON.parse(data.body) : data.body;
      if (data.statusCode === 200 && Array.isArray(body.records)) {
        // const allowedDatasets = JSON.parse(localStorage.getItem('winterSoldierDatasets') || '[]');

        // const filteredDatasets = body.records.filter(dt =>
        //   allowedDatasets.includes(dt.datasetID)
        // );
        const accessLevel = localStorage.getItem('accessLevel');
        const isAdmin = accessLevel === '1' || accessLevel === 'Admin';
        const allowedDatasets = JSON.parse(localStorage.getItem('winterSoldierDatasets') || '[]');

        const filteredDatasets = isAdmin
          ? body.records
          : body.records.filter(record => allowedDatasets.includes(record.datasetID));
        setDatasetList(filteredDatasets);

        const storedDataset = localStorage.getItem("selectedDataset");

        if (filteredDatasets.length > 0) {
          const isStoredValid = filteredDatasets.some(
            ds => ds.datasetID === storedDataset
          );

          const datasetToSet = isStoredValid ? storedDataset : filteredDatasets[0].datasetID;
          setSelectedDataset(datasetToSet);
          localStorage.setItem("selectedDataset", datasetToSet);
        }

        const requestDatasets = filteredDatasets
          .filter(dt => dt.entryType === "Request Based PDF Entry")
          .map(dt => dt.datasetID);

        setRequestBasedDatasets(requestDatasets);

      }
      else {
        showAlert(body?.message || "Failed to load Datasets.", [{ label: "OK", onClick: () => { }, color: 'var(--buttonColor)' }]);
        insertLog(
          `${userName} failed to fetch datasets`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          '',
          'GET',
          'AP_Dataset_List_GET',
          'FAILED',
          'Failed to load datasets'
        );
      }
    } catch (err) {
      insertLog(
        `${userName} failed to fetch datasets`,
        0,
        useremail,
        hud,
        stopHudRotation,
        showAlert,
        '',
        'GET',
        'AP_Dataset_List_GET',
        'FAILED',
        err.message
      );
      showAlert(err.message || "Something went wrong while fetching Datasets.", [{ label: "OK", onClick: () => { }, color: 'var(--buttonColor)' }]);
    }
  };

  const navigateToEdit = (id) => {
    localStorage.setItem('id', id);
    localStorage.removeItem("cloneId");
    localStorage.removeItem("isClone");
    localStorage.setItem("agencyPage", currentPage);
    localStorage.setItem("restoreAgencyPage", "true");
    navigate('/add-agencyDetails');
  };

  const navigateToClone = (id) => {
    localStorage.setItem("cloneId", id);
    localStorage.setItem("isClone", "true");
    navigate("/add-agencyDetails");
  };

  const handlePageChange = async (page) => {
    setCurrentPage(page);
    if (!agencyByPage[page]) {
      fetchAllAgencies(page, pageSize, false, selectedDataset, activeSearch);
    }

  };

  const renderSortIcon = (columnKey) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return <LuTriangle size={10} />;
    }

    return sortConfig.order === "asc" ? (
      <TbTriangleFilled size={10} color="#EC6438" />
    ) : (
      <TbTriangleInvertedFilled size={10} color="#EC6438" />
    );
  };



  const totalPages = Math.ceil(totalRecords / pageSize);

  const agencies = (agencyByPage[currentPage] || []);


  const sortedAgencies = sortConfig
    ? [...agencies].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      const aStr = String(aVal);
      const bStr = String(bVal);

      return sortConfig.order === "asc"
        ? aStr.localeCompare(bStr, undefined, {
          numeric: true,
          sensitivity: "base",
        })
        : bStr.localeCompare(aStr, undefined, {
          numeric: true,
          sensitivity: "base",
        });
    })
    : agencies


  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (!prev || prev.key !== key) {
        return { key, order: "asc" };
      }
      return {
        key,
        order: prev.order === "asc" ? "desc" : "asc",
      };
    });
  };

  const handleAgencySearch = () => {
    if (!selectedDataset) {
      showAlert("Please select a dataset before searching.", [
        { label: "OK", onClick: () => { }, color: 'var(--buttonColor)' }
      ]);
      return;
    }

    const searchText = search.trim();
    setActiveSearch(searchText);
    setCurrentPage(1);
    setAgencyByPage({});
    setLoadedPages([]);
    setHasMorePages(true);
    setTotalRecords(0);
    fetchAllAgencies(1, pageSize, true, selectedDataset, searchText);
  };

  const handleAgencySearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAgencySearch();
    }
  };


  const handleViewClick = (agencyID, datasetID) => {
    navigate("/RequestSentDetails", {
      state: {
        agencyIDfromList: agencyID,
        datasetIDfromList: datasetID
      }
    })
  };


  const handlePageSizeChange = async value => {
    const newSize = Number(value);

    // reset everything
    setPageSize(newSize);
    setCurrentPage(1);
    setAgencyByPage({});
    setLoadedPages([]);
    setHasMorePages(true);
    setTotalRecords(0);
    fetchAllAgencies(1, newSize, true, selectedDataset, activeSearch);
  };

  const startEntry = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;

  const endEntry = Math.min(currentPage * pageSize, totalRecords);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importJson, setImportJson] = useState("");
  const [jsonError, setJsonError] = useState("");
  const [importDataset, setImportDataset] = useState("");
  const [datasetError, setDatasetError] = useState("");
  const useremail = localStorage.getItem("email");
  const userName = useremail?.split("@")[0];
  const handleImportFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith(".json")) {
      showAlert("Only JSON files are allowed", [
        { label: "Ok", color: "var(--buttonColor)", onClick: () => { } }
      ]);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target.result);
        setImportJson(JSON.stringify(parsed, null, 2));
        setJsonError("");
      } catch {
        setJsonError("Invalid JSON file");
      }
    };
    reader.readAsText(file);
  };
  useEffect(() => {
    if (!importJson || importJson.trim() === "") {
      setJsonError("");
      return;
    }

    try {
      JSON.parse(importJson);
      setJsonError("");
    } catch {
      setJsonError("Invalid JSON format");
    }
  }, [importJson]);

  const saveImportedAgencies = async (agencies) => {
    try {
      hud("Saving agencies...");
      console.log("agenciesJSON", agencies)
      const loggedInEmail =
        localStorage.getItem("email") ||
        sessionStorage.getItem("email");
      const response = await agenciesDS.WS_Agency_Bulk_INSERT({
        agencies,
        created_by_email_id: loggedInEmail
      });
      const data = response.data;
      const body = typeof data.body === "string" ? JSON.parse(data.body) : data.body;

      if (data.statusCode === 200) {
        showToast({ message: "Agencies Imported successfully" });
        insertLog(
          `${userName} imported agencies for dataset ${importDataset || "All Datasets"}`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          '',
          'INSERT',
          'WS_Agency_Bulk_INSERT',
          'SUCCESS',
          ''
        );
        setCurrentPage(1);
        setAgencyByPage({});
        setLoadedPages([]);
        setHasMorePages(true);
        fetchAllAgencies(1, pageSize, true, selectedDataset);

        setShowImportModal(false);
        setImportJson("");
        setJsonError("");
        setImportDataset("");
        setDatasetError("");
      } else {
        showAlert(body?.message || "Failed to import agencies", [
          { label: "OK", color: "var(--buttonColor)", onClick: () => { } }
        ]);

        insertLog(
          `${userName} failed to import agencies for dataset ${importDataset || "All Datasets"}`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          '',
          'INSERT',
          'WS_Agency_Bulk_INSERT',
          'FAILED',
          'Failed to import agencies'
        );

      }
    } catch (err) {
      insertLog(
        `${userName} failed to import agencies for dataset ${importDataset || "All Datasets"}`,
        0,
        useremail,
        hud,
        stopHudRotation,
        showAlert,
        '',
        'INSERT',
        'WS_Agency_Bulk_INSERT',
        'FAILED',
        err.message
      );
      showAlert(
        err.message || "Something went wrong while importing agencies",
        [{ label: "OK", color: "var(--buttonColor)", onClick: () => { } }]
      );
    } finally {
      stopHudRotation();
    }
  };

  return (
    <div className="main-content">
      <div className="custom-header">
        <span className="title">Agencies</span>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={() => {
              setShowImportModal(true);
              setImportJson("");
              setJsonError("");
              setImportDataset("");
              setDatasetError("");
            }}
          >
            Import Agencies
          </button>


          <button onClick={addAgencies}>
            <IoMdAdd /> Add Agencies
          </button>
        </div>
      </div>

      <div className="mt-3 ">

        <div className="dataset-pag">
          <div className="dataset-drop">
            <div
              className="agency-dataset-search-select"
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                  setIsDatasetDropdownOpen(false);
                }
              }}
            >
              <input
                type="text"
                className="form-control"
                value={datasetSearch}
                onChange={handleDatasetSearchChange}
                onFocus={(event) => {
                  setIsDatasetDropdownOpen(true);
                  setShowAllDatasetOptions(false);
                  event.target.select();
                }}
                onKeyDown={handleDatasetKeyDown}
                placeholder="Select Dataset"
                autoComplete="off"
              />

              <button
                className="agency-dataset-search-select-toggle"
                type="button"
                onClick={() => {
                  if (!showAllDatasetOptions) {
                    setShowAllDatasetOptions(true);
                    setIsDatasetDropdownOpen(true);
                    return;
                  }
                  setShowAllDatasetOptions(false);
                  setIsDatasetDropdownOpen(false);
                }}
                aria-label="Toggle dataset options"
              >
                <FaChevronDown />
              </button>

              {isDatasetDropdownOpen && (
                <div className="agency-dataset-search-select-menu">
                  {filteredDatasetOptions.length > 0 ? (
                    filteredDatasetOptions.map((dataset, index) => (
                      <button
                        key={`${dataset.datasetID || getDatasetLabel(dataset)}-${index}`}
                        type="button"
                        className={`agency-dataset-search-select-option ${selectedDataset === dataset.datasetID ? "selected" : ""}`}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => selectDatasetOption(dataset)}
                      >
                        {getDatasetLabel(dataset)}
                      </button>
                    ))
                  ) : (
                    <div className="agency-dataset-search-select-empty">
                      No datasets found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="dataset-search">
            <CiSearch className="dataset-search-icon" />

            <input
              type="text"
              placeholder="Search by Agency ID / Agency County Name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleAgencySearchKeyDown}
            />
          </div>

          <button
            className="agency-search_icon"
            type="button"
            onClick={handleAgencySearch}
            disabled={!selectedDataset}
            aria-label="Search agencies"
            title="Search agencies"
          >
            <CiSearch />
          </button>

          <div className="pagination-wrapper-agen">
            <span className="showing-text">
              Showing {startEntry} to {endEntry} of {totalRecords} entries
            </span>

            <span>Show</span>
            <select
              className="form-select"
              value={pageSize}
              disabled={!isPageSizeEnabled}
              onChange={(e) => handlePageSizeChange(e.target.value)}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>

            <ul className="pagination-req">
              <li>
                <button
                  className="page-btn-req"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  &lt;
                </button>
              </li>
              <li>
                <button className="page-btn-req page-btn-active">{currentPage}</button>
              </li>
              <li>
                <button
                  className="page-btn-req"
                  disabled={currentPage === totalPages || agencies.length === 0}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  &gt;
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="table-content-agen mt-3">
          <div className="agen-table-wrapper">
            <table className="agency-table">
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort("agencyID")}
                    className="sortable-th"
                  >
                    <span className="th-content">
                      <span>Agency Id</span>
                      {renderSortIcon("agencyID")}
                    </span>
                  </th>

                  <th>Agency County Name</th>
                  <th>Agency Display Name</th>
                  <th>Phone</th>
                  <th>Contact Person</th>
                  <th>HandledByUser</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {sortedAgencies.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center no-records-td">No Records Found</td>
                  </tr>
                ) : (
                  sortedAgencies.map((item, index) => (
                    <tr key={item.id || index}>
                      <td
                        style={{
                          color: requestBasedDatasets.includes(selectedDataset) ? "#09c" : "inherit",
                          cursor: requestBasedDatasets.includes(selectedDataset) ? "pointer" : "default"
                        }}
                        onClick={() => {
                          if (!requestBasedDatasets.includes(selectedDataset)) {
                            return;
                          }
                          const payload = {
                            agency: item,
                            dataset: selectedDatasetRecord || null,
                          };
                          localStorage.setItem("agencyDetailsPayload", JSON.stringify(item));
                          const url = `/AgencyDetailPage`;
                          window.open(url, "_blank");
                        }}
                      >
                        {item.agencyID || "NA"}
                      </td>
                      {/* <td>{item.datasetID || "NA"}</td> */}
                      < td > {item.agencyCountyName || "NA"}</td>
                      <td>{item.agencyDisplayName || "NA"}</td>
                      <td>{item.agencyPhoneNumber || "NA"}</td>
                      <td>{item.agencyContactPerson || "NA"}</td>
                      <td>{item.handledByUser}</td>
                      <td >
                        <span className="action-icon">


                          {requestBasedDatasets.includes(item.datasetID) && <GoProjectSymlink className="navigation-icon" onClick={() => handleViewClick(item.agencyID, item.datasetID)} />}

                          <>
                            <FaEdit onClick={() => navigateToEdit(item.id)} />

                            <FaClone
                              onClick={() => navigateToClone(item.id)}
                              title="Clone Agency"
                            />
                          </>
                        </span>

                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

        </div>
        {
          showImportModal && (
            <div className="import-modal">
              <div className="import-body">

                {/* HEADER */}
                <div className="import-header">
                  <span>Import Agencies</span>
                  <MdCancel
                    size={24}
                    className="import-close"
                    onClick={() => {
                      setShowImportModal(false);
                      setImportJson("");
                      setJsonError("");
                      setImportDataset("");
                      setDatasetError("");
                    }}
                  />
                </div>

                {/* BODY */}
                <div className="import-content">

                  {/* DATASET DROPDOWN */}
                  <div className="import-row">
                    {/* DATASET */}
                    <div className="import-col">
                      <label>Datasets</label>
                      <select
                        className={`form-select import-Datasets ${datasetError ? "import-error" : ""}`}
                        value={importDataset}
                        onChange={(e) => {
                          setImportDataset(e.target.value);
                          setDatasetError("");
                        }}
                      >
                        <option value="">All Datasets</option>
                        {datasetList.map((ds, idx) => (
                          <option key={idx} value={ds.datasetID}>
                            {ds.datasetName || ds.datasetID}
                          </option>
                        ))}
                      </select>
                      {datasetError && (
                        <p className="import-error-text">{datasetError}</p>
                      )}
                    </div>

                    {/* FILE UPLOAD */}
                    <div className="import-col">
                      <label>Upload Json File</label>
                      <input
                        type="file"
                        accept=".json"
                        className="form-control import-choosefile"
                        onChange={handleImportFile}
                      />
                    </div>
                  </div>
                  {/* JSON TEXTAREA */}
                  <label>JSON</label>
                  <textarea
                    placeholder="Enter Json Schema"
                    className={`import-json form-control ${jsonError ? "import-error" : ""}`}
                    value={importJson}
                    onChange={(e) => {
                      setImportJson(e.target.value);
                    }}
                  />
                  {jsonError && (
                    <p className="import-error-text">{jsonError}</p>
                  )}

                  {/* BUTTON */}
                  <div className="import-footer">
                    <button
                      className="import-btn"
                      onClick={() => {
                        try {
                          setDatasetError("");
                          setJsonError("");
                          if (!importDataset) {
                            setDatasetError("Dataset is required");
                          }
                          if (!importJson || importJson.trim() === "") {
                            setJsonError("JSON is required");
                          }
                          if (!importDataset || !importJson.trim()) {
                            return;
                          }
                          let agencies = JSON.parse(importJson.trim());
                          if (!Array.isArray(agencies)) agencies = [agencies];
                          for (let agency of agencies) {
                            if (typeof agency !== "object" || Array.isArray(agency)) {
                              throw new Error("Invalid agency structure");
                            }
                            if (!agency.agencyID) {
                              throw new Error("agencyID is required");
                            }
                            agency.datasetID = importDataset;
                          }
                          saveImportedAgencies(agencies);
                        } catch (err) {
                          setJsonError(err.message || "Invalid agency JSON");
                        }
                      }}
                    >
                      Save Agencies
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        }
      </div >

    </div >
  );
};

export default Agencies;
